import type { Guia } from '../tipos';

export const guiaPuericultura: Guia = {
  id: 'puericultura',
  titulo: 'Puericultura: seguimento da criança exposta',
  resumo:
    'Iniciar o seguimento, coletas, consultas, encaminhamentos e o encerramento aos 18 meses ou antes.',
  perfis: ['enfermeiro', 'medico'],
  revisadoEm: '2026-10-17',
  secoes: [
    {
      id: 'iniciar',
      titulo: 'Iniciar o seguimento',
      passos: [
        'Na página do recém-nascido, clique em **Iniciar seguimento**. A criança passa a ter a própria ficha, com nome, CPF ou CNS.',
        'O sistema monta a agenda do protocolo: VDRL com 1, 3, 6, 12 e 18 meses, consultas e encaminhamentos iniciais.',
      ],
      observacao:
        'No painel **Puericultura**, o cartão "Aguardando o início do seguimento" lista os recém-nascidos que ainda não começaram.',
      botoes: ['Iniciar seguimento'],
      imagem: {
        arquivo: 'puericultura-painel.png',
        descricao:
          'Painel da puericultura com as crianças em seguimento e as que aguardam o início.',
      },
    },
    {
      id: 'registrar',
      titulo: 'Coletas, consultas e encaminhamentos',
      passos: [
        'Na ficha da criança, a aba **Seguimento** mostra a agenda, com o que está previsto, atrasado ou feito.',
        'Na aba **Exames**, use **Registrar exame**; na aba **Consultas**, **Registrar consulta** (peso, comprimento, perímetro cefálico).',
        'Na aba **Encaminhamentos**, use **Novo encaminhamento** e, depois, **Andamento** para registrar o que aconteceu.',
        'Item que não se aplica: **Cancelar item**, com o motivo.',
      ],
      observacao:
        'O sistema alerta se o título subir 2 diluições, se o teste continuar reagente depois do esperado ou se a criança ficar muito tempo sem registro.',
      botoes: ['Registrar exame', 'Registrar consulta', 'Novo encaminhamento', 'Andamento'],
      imagem: {
        arquivo: 'puericultura-ficha.png',
        descricao: 'Ficha da criança com as abas Seguimento, Exames, Consultas e Encaminhamentos.',
      },
    },
    {
      id: 'encerrar',
      titulo: 'Encerrar o seguimento',
      passos: [
        'A definição final do caso ocorre aos 18 meses. O sistema sugere a classificação (por exemplo, sífilis congênita descartada) com a explicação.',
        'O médico confere e clica em **Encerrar seguimento**. Antes do fim, use **Encerrar antes do fim do seguimento** (óbito, mudança de município, perda de seguimento), com justificativa.',
      ],
      observacao: 'Só o médico encerra o seguimento.',
      botoes: ['Encerrar seguimento', 'Encerrar antes do fim do seguimento'],
    },
  ],
};

export const guiaNotificacoes: Guia = {
  id: 'notificacoes',
  titulo: 'Notificações SINAN',
  resumo:
    'Revisar a ficha preenchida pelo sistema, completar o que faltar, gerar o PDF e registrar o número.',
  perfis: ['enfermeiro', 'medico', 'gestor'],
  revisadoEm: '2026-10-17',
  secoes: [
    {
      id: 'lista',
      titulo: 'De onde vêm as notificações',
      passos: [
        'O sistema cria a notificação pendente sozinho: teste reagente de sífilis ou HIV na gestante, sífilis no parceiro e sífilis congênita no recém-nascido.',
        'No menu, abra **Notificações SINAN** para ver todas, ou a aba **Notificações** da ficha da gestante.',
      ],
      imagem: {
        arquivo: 'sinan-lista.png',
        descricao: 'Lista de notificações SINAN com o tipo, a situação e a unidade.',
      },
    },
    {
      id: 'revisar',
      titulo: 'Revisar e completar',
      passos: [
        'Abra a notificação: cada campo da ficha mostra o valor e de onde ele veio (cadastro, exames, tratamento).',
        'Complete os campos em branco e clique em **Salvar campos preenchidos**.',
        'O sistema avisa quando há campos obrigatórios em branco.',
      ],
      botoes: ['Salvar campos preenchidos'],
      imagem: {
        arquivo: 'sinan-revisao.png',
        descricao:
          'Revisão da ficha SINAN com os campos, a origem de cada valor e o aviso de obrigatórios.',
      },
    },
    {
      id: 'gerar',
      titulo: 'Gerar a ficha e registrar o número',
      passos: [
        'Clique em **Gerar ficha (PDF)**: sai a ficha oficial preenchida, para imprimir ou digitar no SINAN. Com obrigatórios em branco, é preciso confirmar em **Gerar mesmo assim**.',
        'Depois de digitar no SINAN, informe o **Nº SINAN** e clique em **Salvar número**: a notificação passa a "Notificada".',
        'Para baixar de novo, use **Baixar a última ficha**.',
      ],
      observacao:
        'Notificação criada por engano: **Cancelar notificação**, com o motivo. Ela continua registrada.',
      botoes: [
        'Gerar ficha (PDF)',
        'Gerar mesmo assim',
        'Salvar número',
        'Baixar a última ficha',
        'Cancelar notificação',
      ],
    },
  ],
};

export const guiaAgenda: Guia = {
  id: 'agenda',
  titulo: 'Agenda, pendências e páginas Sífilis e HIV',
  resumo:
    'Doses, coletas e consultas por período, o que está atrasado e a visão de cada agravo no município.',
  perfis: ['enfermeiro', 'medico', 'gestor', 'recepcao'],
  revisadoEm: '2026-10-17',
  secoes: [
    {
      id: 'agenda',
      titulo: 'Agenda do período',
      passos: [
        'No menu, abra **Agenda e pendências** e escolha o **Período** (hoje, próximos 7 ou 30 dias, ou as datas).',
        'Marque **Incluir os atrasados** para ver o que passou do prazo.',
        'Use **Imprimir** para a lista do dia, ou **Baixar planilha (CSV)** para abrir no Excel.',
      ],
      observacao:
        'A recepção vê a agenda sem a doença ("Dose agendada"), para não expor o diagnóstico. As pendências clínicas ficam com a equipe de saúde.',
      botoes: ['Imprimir', 'Baixar planilha (CSV)'],
      imagem: {
        arquivo: 'agenda.png',
        descricao: 'Agenda do período com as doses, coletas e consultas e a situação de cada uma.',
      },
    },
    {
      id: 'agravos',
      titulo: 'Páginas Sífilis e HIV',
      passos: [
        'Em **Agravos acompanhados**, abra **Sífilis** ou **HIV**: aparecem as gestantes do município com o agravo, do pré-natal ao seguimento da criança.',
        'Os números do topo resumem a situação (tratamento, parceiro, notificação). Use **Fase** e **Situação** para filtrar.',
        'Clique no nome para abrir a ficha.',
      ],
      observacao:
        'Mães no pós-parto aparecem até 6 meses depois do parto ou enquanto a criança está em seguimento.',
      imagem: {
        arquivo: 'agravos-sifilis.png',
        descricao:
          'Página Sífilis com os contadores e a tabela das gestantes com a situação de cada etapa.',
      },
    },
  ],
};
